import type { ChatRoomFilterInput } from '@/types/schema.generated'
import { useApolloClient } from '@vue/apollo-composable'
import { useDocumentVisibility } from '@vueuse/core'
import type { Ref } from 'vue'
import { computed, ref, watch, watchEffect } from 'vue'
import type {
  FetchChatRoomsOnUseOwnMatchesQuery,
  OwnChatRoomListQuery,
} from './useOwnMatches.generated'
import {
  FetchChatRoomsOnUseOwnMatchesDocument,
  useOwnChatRoomListLazyQuery,
} from './useOwnMatches.generated'

import { useChatRoomFilterResult } from '../useChatRoomFilterResult'
import { useUpdatedChatRoom } from './useUpdatedChatRoom'

const ROOMS_PER_PAGE = 30

type ChatRoomEdge = NonNullable<
  NonNullable<NonNullable<OwnChatRoomListQuery['chatRooms']>['edges']>[number]
>
type ChatRoomNode = ChatRoomEdge['node']

export function useOwnMatches(
  viewerId: string,
  entranceGUID: string,
  filterInput: Ref<ChatRoomFilterInput> = ref({})
) {
  const { client: apolloClient } = useApolloClient()
  const variables = computed(() => ({
    first: ROOMS_PER_PAGE,
    filter: filterInput.value,
  }))

  const { load, result, fetchMore, error, refetch, loading } =
    useOwnChatRoomListLazyQuery(variables, {
      fetchPolicy: 'no-cache',
    })

  const chatRooms = ref<ChatRoomNode[]>([])
  watchEffect(() => {
    chatRooms.value =
      result.value?.chatRooms?.edges
        ?.filter((item): item is NonNullable<typeof item> => !!item)
        .map(item => item.node) || []
  })

  const documentVisibility = useDocumentVisibility()

  const updatedChatRoomIds = ref<string[]>([])

  watch(
    () => [updatedChatRoomIds.value, documentVisibility.value],
    async ([_chatRoomIds, _documentVisibility]) => {
      if (_chatRoomIds.length === 0) {
        return
      }

      if (_documentVisibility !== 'visible') {
        return
      }

      updatedChatRoomIds.value = []

      const chatRoomIds = Array.from(new Set(_chatRoomIds))
      // チャット一括送信によって、大量のチャットルームが更新されることがある（例えば OA は 150件一気に送るとか）
      // 予想しない大きな数字で送られたときの処理が怖いので、
      // ある程度の量がまとめて更新されたときはもうリストごとまとめて更新してしまう
      if (chatRoomIds.length >= 100) {
        refetch()
        return
      }

      const result =
        await apolloClient.query<FetchChatRoomsOnUseOwnMatchesQuery>({
          query: FetchChatRoomsOnUseOwnMatchesDocument,
          variables: {
            ids: chatRoomIds,
          },
          // updatedChatRoomIds が更新されたとき、チャットルームの更新があったということなのでキャッシュを使わない
          fetchPolicy: 'no-cache',
        })

      const updatedChatRooms = result.data.chatRoomsByIDs

      for (const room of updatedChatRooms) {
        const index = chatRooms.value.findIndex(r => r.id === room.id)

        if (index === -1) {
          chatRooms.value = [...chatRooms.value, room]
        }

        chatRooms.value = [
          ...chatRooms.value.slice(0, index),
          room,
          ...chatRooms.value.slice(index + 1),
        ]
      }
    }
  )

  const onUpdateChatRoom = async (chatRoomIds: string[]) => {
    updatedChatRoomIds.value = [...updatedChatRoomIds.value, ...chatRoomIds]
  }

  const mutate = async () => {
    if (result.value) {
      await refetch({
        first: result.value.chatRooms?.edges?.length ?? ROOMS_PER_PAGE,
      })
    }
  }

  const useUpdatedChatRoomConnectedOnce = ref(false)
  useUpdatedChatRoom(viewerId, entranceGUID, onUpdateChatRoom, () => {
    // 一度繋がったあと、再度繋がっているということは切断されていた期間があったということ
    // 切断されていた期間があったということは、onUpdateChatRoom が呼ばれていないチャットルーム更新があったかもしれないので、
    // リストを再取得する
    if (useUpdatedChatRoomConnectedOnce.value) {
      mutate()
    }

    useUpdatedChatRoomConnectedOnce.value = true
  })

  const { matches } = useChatRoomFilterResult(chatRooms, filterInput)

  const isLoadingNextPage = ref(false)

  const hasNextPage = computed(
    () => result.value?.chatRooms?.pageInfo?.hasNextPage
  )

  const initialError = computed(() =>
    error.value ? 'エラーが発生しました' : null
  )

  const wrappedFetchMore = async () => {
    if (isLoadingNextPage.value) return

    isLoadingNextPage.value = true
    try {
      await fetchMore({
        variables: {
          first: ROOMS_PER_PAGE,
          after: result.value?.chatRooms?.pageInfo.endCursor,
        },
        updateQuery: (
          previousData,
          { fetchMoreResult }
        ): OwnChatRoomListQuery => {
          if (!fetchMoreResult || !fetchMoreResult.chatRooms) {
            return previousData
          }

          return {
            ...previousData,
            chatRooms: {
              ...previousData.chatRooms,
              edges: [
                ...(previousData.chatRooms?.edges ?? []),
                ...(fetchMoreResult.chatRooms?.edges ?? []),
              ],
              pageInfo: fetchMoreResult.chatRooms?.pageInfo,
            },
          }
        },
      })
    } finally {
      isLoadingNextPage.value = false
    }
  }

  return {
    load,
    data: matches,
    error: initialError,
    isLoadingNextPage,
    loading,
    mutate,
    fetchMore: wrappedFetchMore,
    hasNextPage,
    onUpdateChatRoom,
  }
}
